import { useCallback } from "react";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { reorderVisibleBlocks, type VisibleBlock } from "../utils/reorder-visible-blocks";
import { useResolvedView } from "./use-resolved-view";
import { useWritableViewId, type UseWritableViewIdArgs } from "./use-writable-view-id";

export interface UseReorderBlocksArgs {
  viewId: string;
  onViewMaterialized?: UseWritableViewIdArgs["onViewMaterialized"];
}

export interface ReorderBlocksArgs {
  visibleBlocks: VisibleBlock[];
  activeId: string;
  overId: string;
}

export const useReorderBlocks = ({ viewId, onViewMaterialized }: UseReorderBlocksArgs) => {
  const resolvedView = useResolvedView(viewId);

  const updateViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "updateOne",
    optimisticUpdates: true,
    autoCacheUpdateStrategy: "update",
  });

  const resolveWritableViewId = useWritableViewId({ viewId, onViewMaterialized });

  type MutateOptions = Parameters<typeof updateViewMutation.mutateAsync>[1];

  return useCallback(
    async ({ visibleBlocks, activeId, overId }: ReorderBlocksArgs, options?: MutateOptions) => {
      if (resolvedView.mode === "loading" || resolvedView.mode === "error") return;

      const { blocks } = resolvedView;
      const reordered = reorderVisibleBlocks({ blocks, visibleBlocks, activeId, overId });
      if (reordered === blocks) return;

      const writableView = await resolveWritableViewId();

      return updateViewMutation.mutateAsync(
        {
          id: writableView.id,
          blocks: reordered,
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [resolveWritableViewId, resolvedView, updateViewMutation]
  );
};
